import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  EventSeat as LobbyIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CompleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Note as NoteIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import './Lobby.css';

function Lobby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lobbyEntries, setLobbyEntries] = useState([]);
  const [stats, setStats] = useState({ waiting: 0, inProgress: 0, completedToday: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEntries = lobbyEntries.filter(entry => {
    const firstName = entry.patient?.firstName || '';
    const lastName = entry.patient?.lastName || '';
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    const patientId = (entry.patient?.patientId || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || patientId.includes(search);
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchLobbyEntries();
    fetchStats();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchLobbyEntries();
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLobbyEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/lobby`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLobbyEntries(response.data);
    } catch (err) {
      console.error('Failed to fetch lobby entries:', err);
      setError('Failed to load waiting area');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/lobby/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleStatusChange = async (entryId, newStatus) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/lobby/${entryId}/status`,
        {
          status: newStatus,
          completedBy: newStatus === 'Completed' ? user?.name : null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`Patient marked as ${newStatus}`);
      fetchLobbyEntries();
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNotes = (entry) => {
    setSelectedEntry(entry);
    setNotes(entry.notes || '');
    setNotesDialogOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedEntry) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/lobby/${selectedEntry.id}/notes`,
        { notes },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Notes updated successfully');
      setNotesDialogOpen(false);
      fetchLobbyEntries();
    } catch (err) {
      console.error('Failed to update notes:', err);
      setError('Failed to update notes');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Waiting':
        return 'warning';
      case 'InProgress':
        return 'info';
      case 'Completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getVisitTypeColor = (visitType) => {
    return visitType === 'Visitation' ? 'primary' : 'secondary';
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getWaitingTime = (arrivalTime) => {
    const arrival = new Date(arrivalTime);
    const now = new Date();
    const diffMinutes = Math.floor((now - arrival) / 1000 / 60);

    if (diffMinutes < 60) {
      return `${diffMinutes} min`;
    }
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = (date) => {
    const hour = date.getHours();
    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else if (hour >= 17 && hour < 21) {
      return 'Good Evening';
    } else {
      return 'Good Night';
    }
  };

  const formattedCurrentDate = currentDateTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleViewPatient = (patientId) => {
    navigate(`/patient/${patientId}`);
  };

  const handleRefresh = () => {
    fetchLobbyEntries();
    fetchStats();
    setSuccess('Refreshed successfully');
    setTimeout(() => setSuccess(''), 2000);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Top Greeting & Date (Below Header, Above Waiting Area) */}
      <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0', letterSpacing: '-0.01em' }}>
            {getGreeting(currentDateTime)}{user?.name ? `, ${user.name}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.3, display: 'flex', alignItems: 'center', gap: 0.6, fontWeight: 500 }}>
            <CalendarIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            {formattedCurrentDate}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LobbyIcon /> Waiting Area
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Statistics Cards */}
      <div className="lobby-stats">
        <div className="stat-card waiting">
          <p style={{ margin: '0 0 5px 0' }}>Waiting</p>
          <h3>{stats.waiting}</h3>
        </div>
        <div className="stat-card in-progress">
          <p style={{ margin: '0 0 5px 0' }}>In Progress</p>
          <h3>{stats.inProgress}</h3>
        </div>
        <div className="stat-card completed">
          <p style={{ margin: '0 0 5px 0' }}>Total Today</p>
          <h3>{stats.completedToday}</h3>
        </div>
        <div className="stat-card">
          <p style={{ margin: '0 0 5px 0' }}>Active</p>
          <h3>{stats.total}</h3>
        </div>
      </div>

      {/* Lobby Entries Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ m: 0 }}>
            Current Patients
          </Typography>
          {lobbyEntries.length > 0 && (
            <TextField
              size="small"
              placeholder="Search by patient name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 300 }}
            />
          )}
        </Box>

        {lobbyEntries.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No patients in waiting area
          </Alert>
        ) : filteredEntries.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No patients found matching "{searchTerm}"
          </Alert>
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Patient</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Arrival Time</strong></TableCell>
                  <TableCell><strong>Waiting Time</strong></TableCell>
                  <TableCell><strong>Added By</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {entry.patient.firstName} {entry.patient.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {entry.patient.patientId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.visitType}
                        color={getVisitTypeColor(entry.visitType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status}
                        color={getStatusColor(entry.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(entry.arrivalTime || entry.createdAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        {formatTime(entry.arrivalTime)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          getWaitingTime(entry.arrivalTime).includes('h')
                            ? 'error.main'
                            : 'textPrimary'
                        }
                      >
                        {getWaitingTime(entry.arrivalTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.markedBy}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenNotes(entry)}
                        color={entry.notes ? 'primary' : 'default'}
                      >
                        <NoteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewPatient(entry.patient.id)}
                        >
                          View
                        </Button>
                        {entry.status === 'Waiting' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="info"
                            onClick={() => handleStatusChange(entry.id, 'InProgress')}
                            disabled={loading}
                          >
                            Start
                          </Button>
                        )}
                        {entry.status === 'InProgress' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CompleteIcon />}
                            onClick={() => handleStatusChange(entry.id, 'Completed')}
                            disabled={loading}
                          >
                            Complete
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Patient Notes
          {selectedEntry && (
            <Typography variant="caption" display="block" color="textSecondary">
              {selectedEntry.patient.firstName} {selectedEntry.patient.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this visit..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNotes} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Lobby;
